/**
 * CrewProfileScreen
 * Full-screen 2D overlay — "Artemis II Mission Crew". Shows the 4 crew members as
 * side-profile cards; hovering swaps to front-profile and greys out the others.
 * Selecting a member opens CrewMemberDetail with their bio.
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { CrewMemberCard } from './CrewMemberCard'
import { CrewMemberDetail } from './CrewMemberDetail'
import { Subheading } from './Subheading'
import DSL_SmallWindow from './DSL_SmallWindow'
import { ArtemisMediaLightbox } from '../artemis/ArtemisMediaLightbox'
import { trackEvent } from '../../utils/analytics'
import { playSound } from '../../utils/soundFX'
import crewProfiles from '../../data/crewProfiles.json'
import crewPhotos from '../../data/crewPhotos.json'

export function CrewProfileScreen({ onClose, onHome, mediaQueryKey, hideContextual, onCloseJoinMission }) {
  const isMobilePortrait = mediaQueryKey === 'mobilePortrait'
  const [hoveredCrewId, setHoveredCrewId] = useState(null)
  const [selectedCrewId, setSelectedCrewId] = useState(null)
  const [showPhotos, setShowPhotos] = useState(false)

  const openPhotos = useCallback(() => {
    playSound('DataPoint_Open')
    trackEvent('crew_photos_open')
    setShowPhotos(true)
  }, [])

  const closePhotos = useCallback(() => {
    playSound('DataPoint_Close')
    trackEvent('crew_photos_close')
    setShowPhotos(false)
  }, [])

  const isDesktopBp = ['tabletLandscape', 'desktop1080', 'desktop1440', 'desktop4k'].includes(mediaQueryKey)
  // On mobile portrait + tablet, the crew photo description is hidden behind an
  // "i" toggle (space is tight); desktop keeps the always-shown boxed caption.
  const isTablet = mediaQueryKey === 'tabletPortrait' || mediaQueryKey === 'tabletLandscape'
  const captionToggle = isMobilePortrait || isTablet
  // Mobile portrait also renders the CONTEXT (PHOTOS) + NAV panels as chamfered
  // windows in the bottom-dock (same as desktop), replacing the old bare mobile
  // buttons. The mobile-portrait bottom-dock CSS positions them (NAV bottom,
  // CONTEXT above). The photos lightbox keeps its own mobile close button, so
  // it stays keyed off isDesktopBp, not this flag.
  const renderAsWindow = isDesktopBp || isMobilePortrait
  // Render a HUD action inside a chamfered DSL_SmallWindow (desktop + mobile
  // portrait) pinned to one of the dock slots, or a bare button on tablet
  // portrait / mobile landscape. Matches the Artemis / nav window pattern.
  const renderHudWindow = (slotClass, label, onClick, leadingChevron) => {
    const btn = (
      <button type="button" className="rect_button-alt nav-window-action" onClick={onClick}>
        <span>{label}</span>
        {!leadingChevron && <span className="rect_button-chevrons">[ ]</span>}
      </button>
    )
    if (renderAsWindow) {
      return (
        <DSL_SmallWindow
          hideCloseButton={true}
          allowButtonClicks={true}
          noBackdrop={true}
          chamferScale={0.6}
          containerClassName={slotClass}
        >
          {btn}
        </DSL_SmallWindow>
      )
    }
    return btn
  }

  // Bottom-right nav window — BACK (closes just the crew screen, or just JOIN
  // US if it's open on top) + HOME (closes everything, back to the app home).
  // Portaled to document.body: .crew-profile-container is `position: fixed`
  // with an explicit z-index, which creates a stacking context, capping any
  // z-index set on a descendant at the container's own value (16777280) no
  // matter how high it's set. Rendering outside that subtree is the only way
  // for this window to appear above siblings like the JOIN US window.
  const renderCrewNavWindow = () => {
    // While the photos lightbox is open, the in-lightbox close button is hidden
    // (desktop) and the nav BACK button becomes CLOSE, dismissing just the
    // photos and returning to the crew grid. In detail view, BACK returns to
    // the crew grid (not to the app) — handled below.
    const isDetailView = !!selectedCrewId
    let backLabel, backOnClick
    if (showPhotos) {
      backLabel = 'CLOSE'
      backOnClick = closePhotos
    } else if (isDetailView) {
      backLabel = 'BACK'
      backOnClick = handleBackToCrew
    } else {
      backLabel = 'BACK'
      backOnClick = hideContextual ? onCloseJoinMission : onClose
    }
    // Mobile portrait: the separate CON (PHOTOS) window is hidden and its PHOTOS
    // button lives here in the NAV window, on the far right (after BACK + HOME).
    // Not shown while the photos lightbox is already open, or when JOIN is up.
    const showInlinePhotos = isMobilePortrait && !showPhotos && !hideContextual
    const actions = (
      <>
        <button type="button" className="rect_button-alt rect_button-back nav-window-action" onClick={backOnClick}>
          <span>{backLabel}</span>
        </button>
        <button type="button" className="rect_button-alt rect_button-back nav-window-action" onClick={() => { onClose(); onHome?.() }}>
          <span>HOME</span>
        </button>
        {showInlinePhotos && (
          <button type="button" className="rect_button-alt nav-window-action" onClick={openPhotos}>
            <span>PHOTOS</span>
            <span className="rect_button-chevrons">[ ]</span>
          </button>
        )}
      </>
    )
    const win = renderAsWindow ? (
      <DSL_SmallWindow
        hideCloseButton={true}
        allowButtonClicks={true}
        noBackdrop={true}
        chamferScale={0.6}
        containerClassName="nav-window crew-nav-window"
      >
        {actions}
      </DSL_SmallWindow>
    ) : actions
    return createPortal(win, document.body)
  }

  // Mobile scroll affordance: track which member the snap stack is on (for the
  // carousel dots).
  const scrollRef = useRef(null)
  const [activeIndex, setActiveIndex] = useState(0)

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setActiveIndex(Math.round(el.scrollTop / el.clientHeight))
  }, [])

  const scrollToPage = useCallback((index) => {
    const el = scrollRef.current
    if (!el) return
    playSound('DataPoint_Open')
    el.scrollTo({ top: index * el.clientHeight, behavior: 'smooth' })
  }, [])

  const handleSelect = useCallback((id) => {
    playSound('DataPoint_Open')
    trackEvent('crew_member_selected', { crew_id: id })
    setSelectedCrewId(id)
  }, [])

  const handleBackToCrew = useCallback(() => {
    playSound('DataPoint_Close')
    trackEvent('crew_member_detail_close')
    setSelectedCrewId(null)
  }, [])

  const selectedIndex = crewProfiles.crew.findIndex(c => c.id === selectedCrewId)
  const selectedMember = crewProfiles.crew[selectedIndex]
  const total = crewProfiles.crew.length
  const prevMember = selectedMember ? crewProfiles.crew[(selectedIndex - 1 + total) % total] : null
  const nextMember = selectedMember ? crewProfiles.crew[(selectedIndex + 1) % total] : null

  // Toggle a body class while in crew detail mode so the HUD side bars (red
  // rectangles near the prev/next nav arrows) can be hidden via CSS. On mobile
  // portrait the screen opens straight into detail, so it's always "detail open".
  useEffect(() => {
    const detailOpen = isMobilePortrait || !!selectedMember
    document.body.classList.toggle('crew-detail-open', detailOpen)
    return () => document.body.classList.remove('crew-detail-open')
  }, [selectedMember, isMobilePortrait])

  const handleNavigate = useCallback((direction) => {
    playSound('DataPoint_Open')
    const total = crewProfiles.crew.length
    const nextIndex = (selectedIndex + direction + total) % total
    const nextMember = crewProfiles.crew[nextIndex]
    trackEvent('crew_member_selected', { crew_id: nextMember.id })
    setSelectedCrewId(nextMember.id)
  }, [selectedIndex])

  // Touch swipe to change crew member in the detail view. Swipe left → next,
  // right → previous (mirrors the prev/next arrows). A horizontal-dominant swipe
  // past the threshold triggers; vertical-dominant gestures are ignored so the
  // bio can still be scrolled.
  const swipeStartRef = useRef(null)

  const handleTouchStart = useCallback((e) => {
    if (!selectedMember) return
    const t = e.touches[0]
    swipeStartRef.current = { x: t.clientX, y: t.clientY }
  }, [selectedMember])

  const handleTouchEnd = useCallback((e) => {
    const start = swipeStartRef.current
    swipeStartRef.current = null
    if (!start || !selectedMember) return
    const t = e.changedTouches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    const SWIPE_THRESHOLD = 50
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return
    // Swipe left (dx < 0) → next member; swipe right (dx > 0) → previous.
    handleNavigate(dx < 0 ? 1 : -1)
  }, [selectedMember, handleNavigate])

  // Mobile portrait: no overview — open straight into a vertical scroll-snap
  // stack of every member's bio, one full screen each.
  if (isMobilePortrait) {
    return (
      <div className="crew-profile-container crew-profile-container--mobile">
        {/* Persistent DSL title at the top of the crew screen (mobile portrait),
            matching the desktop crew title. */}
        <Subheading text="Artemis II Mission Crew" className="dsl-heading--artemis dsl-heading--crew" />
        <div className="crew-profile crew-profile--mobile">
          <div className="crew-detail-scroll" ref={scrollRef} onScroll={handleScroll}>
            {crewProfiles.crew.map((member, i) => (
              <section className="crew-detail-page" key={member.id}>
                <img src={member.imageFront} alt="" className="crew-detail-page-bg" />
                <CrewMemberDetail member={member} contentPosition={i} useBulletsAsFacts />
              </section>
            ))}
          </div>
        </div>
        {/* Scroll affordance — dots indicate the crew members; tap to jump. */}
        <div className="crew-scroll-hint">
          <div className="crew-scroll-hint-dots">
            {crewProfiles.crew.map((member, i) => (
              <button
                key={member.id}
                type="button"
                className={`crew-scroll-hint-dot${i === activeIndex ? ' crew-scroll-hint-dot--active' : ''}`}
                onClick={() => scrollToPage(i)}
                aria-label={`Go to ${member.name}`}
              />
            ))}
          </div>
        </div>

        {/* Mobile portrait: the NAV panel is the single bottom-dock window, with
            PHOTOS added inline on its far right (see renderCrewNavWindow). The
            separate CON (crew-photos) window is NOT rendered here on mobile, and
            the global OPS panel is hidden on the crew screen (see app.jsx). */}
        {renderCrewNavWindow()}

        {/* hideCloseButton: the crew NAV panel's BACK→CLOSE dismisses the photos,
            so the lightbox's own footer CLOSE is suppressed (no duplicate). */}
        {showPhotos && (
          <ArtemisMediaLightbox media={crewPhotos} onClose={closePhotos} isMobilePortrait={isMobilePortrait} hideCloseButton captionToggle={captionToggle} />
        )}
      </div>
    )
  }

  return (
    <div
      className="crew-profile-container"
      onClick={(e) => {
        if (e.target.classList.contains('crew-profile-container')) onClose()
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {!selectedMember && (
        <Subheading text="Artemis II Mission Crew" className="dsl-heading--artemis dsl-heading--crew" />
      )}

      <div className="crew-profile">
        <button type="button" className="crew-profile-close" onClick={onClose}>×</button>

        <div className={`crew-member-row${selectedMember ? ' crew-member-row--detail' : ''}`}>
          {crewProfiles.crew.map(member => (
            <CrewMemberCard
              key={member.id}
              member={member}
              isHovered={selectedMember ? selectedCrewId === member.id : hoveredCrewId === member.id}
              onHover={() => setHoveredCrewId(member.id)}
              onLeave={() => setHoveredCrewId(null)}
              onSelect={() => handleSelect(member.id)}
            />
          ))}
        </div>

        {selectedMember ? (
          <CrewMemberDetail
            key={selectedMember.id}
            member={selectedMember}
            contentPosition={selectedIndex}
          />
        ) : (
          <div className="crew-profile-footer">
            <span className="crew-profile-hint">SELECT A MEMBER OF THE ARTEMIS II CREW TO LEARN MORE</span>
          </div>
        )}
      </div>

      {/* Middle-right slot — PHOTOS. Hidden whenever JOIN US is open, matching
          the other contextual-window panels elsewhere in the app. */}
      {!selectedMember && !hideContextual && renderHudWindow('crew-photos-window', 'PHOTOS', openPhotos, false)}

      {/* Bottom-right nav — own copy, separate from the JOIN US window's
          nav-window (each screen wires BACK to its own close behaviour).
          Always visible: in the grid view BACK closes crew, in the detail
          view BACK returns to the crew grid. */}
      {renderCrewNavWindow()}

      {/* Prev/next crew nav arrows — rendered here (not inside .crew-profile) so
          they escape the panel's clip-path/overflow and can sit at the screen edge */}
      {selectedMember && (
        <>
          <button
            type="button"
            className="crew-detail-nav-arrow crew-detail-nav-arrow--prev"
            onClick={() => handleNavigate(-1)}
          >
            <span className="rect_button-chevrons crew-detail-nav-chevron">&lt;&lt;&lt;</span>
            <span className="crew-detail-nav-label">
              <span className="crew-detail-nav-role">{prevMember.role}</span>
              <span className="crew-detail-nav-name">{prevMember.name}</span>
            </span>
          </button>
          <button
            type="button"
            className="crew-detail-nav-arrow crew-detail-nav-arrow--next"
            onClick={() => handleNavigate(1)}
          >
            <span className="crew-detail-nav-label">
              <span className="crew-detail-nav-role">{nextMember.role}</span>
              <span className="crew-detail-nav-name">{nextMember.name}</span>
            </span>
            <span className="rect_button-chevrons crew-detail-nav-chevron">&gt;&gt;&gt;</span>
          </button>
        </>
      )}

      {showPhotos && (
        <ArtemisMediaLightbox media={crewPhotos} onClose={closePhotos} isMobilePortrait={isMobilePortrait} hideCloseButton={isDesktopBp} captionToggle={captionToggle} />
      )}
    </div>
  )
}

